import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, ExternalLink, Trash2, Globe, Code2 } from "lucide-react";
import { toast } from "sonner";

export default function ResourceEmbeds() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", description: "", kind: "link" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/resources").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/resources", form);
      toast.success("Resource added");
      setOpen(false); setForm({ title: "", url: "", description: "", kind: "link" }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/resources/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <section data-testid="resources-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">AI in HR Implementation Resources</div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0A2A5E]">LUMIS AI™ Academy resources</h2>
          <p className="text-sm text-slate-600 mt-1">Courses, implementation resources, and structured learning pathways from the LUMIS AI™ Academy (lumisai.co). Managers can add links here; embeds render inline when the site allows it.</p>
        </div>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-resource-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><Plus className="w-4 h-4 mr-2" /> Add resource</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Add an Academy resource</DialogTitle>
                <DialogDescription>Paste a lumisai.co course, article, or resource URL. Choose "Embed" to render it inline (the site must allow framing), or "Link" for a card that opens in a new tab.</DialogDescription>
              </DialogHeader>
              <form onSubmit={add} className="space-y-3">
                <div><Label>Title</Label><Input data-testid="resource-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>URL</Label><Input data-testid="resource-url" required type="url" placeholder="https://lumisai.co/..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea data-testid="resource-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <Label>Display</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="resource-kind"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="link">Link card</SelectItem><SelectItem value="embed">Inline embed (iframe)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="resource-submit" type="submit" disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? "Adding…" : "Add"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 && (
        <Card data-testid="resources-empty" className="p-6 bg-white border-dashed border-2 border-[#F5A623]/60 text-sm text-slate-600">
          No resources yet. {isManager ? "Use “Add resource” to add your first LUMIS AI™ Academy resource link." : "Your HR manager will add LUMIS AI™ resources here."}
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((r) => (
          <Card key={r.id} data-testid={`resource-${r.id}`} className="bg-white border-slate-200 overflow-hidden flex flex-col">
            <div className="p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FDECD0] flex items-center justify-center flex-shrink-0">
                {r.kind === "embed" ? <Code2 className="w-5 h-5 text-[#7C3F00]" /> : <Globe className="w-5 h-5 text-[#7C3F00]" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[#0A2A5E] leading-snug">{r.title}</h3>
                {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                  <a href={r.url} target="_blank" rel="noreferrer" data-testid={`resource-link-${r.id}`} className="text-[#0A2A5E] hover:underline inline-flex items-center gap-1 truncate max-w-full">{r.url} <ExternalLink className="w-3 h-3 flex-shrink-0" /></a>
                  <span className="text-slate-400">· Source: {r.source}</span>
                </div>
              </div>
              {isManager && <Button size="sm" variant="ghost" data-testid={`resource-delete-${r.id}`} onClick={() => remove(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></Button>}
            </div>
            {r.kind === "embed" && (
              <div className="border-t border-slate-200 bg-slate-50">
                <iframe title={r.title} src={r.url} data-testid={`resource-embed-${r.id}`} className="w-full h-80" sandbox="allow-scripts allow-same-origin allow-popups" loading="lazy" />
                <div className="px-3 py-1.5 text-[11px] text-slate-500">If the embed is blank, the site blocks framing — use the link above.</div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
